import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { SeatUsageView } from '@tailoring-crm/shared';

/**
 * Usage against a limit, shown BEFORE the limit is reached.
 *
 * The bar is the point. A number on its own ("4 of 10") is read as a fact; a bar that is
 * three-quarters full is read as a warning, and the shop hires with its eyes open instead of
 * discovering the ceiling from an error message halfway through inviting somebody.
 *
 * An unlimited allowance draws no bar at all. A meter that can never fill is decoration, and
 * a Pro shop does not need to be reminded of a limit it does not have.
 */

export interface UsageMeterProps {
  label: string;
  usage: SeatUsageView;
}

export function UsageMeter({ label, usage }: UsageMeterProps): ReactElement {
  const { t } = useTranslation();
  const taken = usage.used + usage.pending;

  if (usage.allowance === null) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium">{label}</span>
          <span className="text-sm text-muted-foreground">
            {t('billing.usage.unlimited', { count: usage.used })}
          </span>
        </div>
      </div>
    );
  }

  const ratio = Math.min(1, taken / usage.allowance);
  const tone = usage.atLimit ? 'bg-destructive' : ratio >= 0.8 ? 'bg-warning' : 'bg-primary';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-sm text-muted-foreground">
          {t('billing.usage.count', { used: taken, allowance: usage.allowance })}
        </span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-muted"
        role="meter"
        aria-label={label}
        aria-valuenow={taken}
        aria-valuemin={0}
        aria-valuemax={usage.allowance}
      >
        <div
          className={`h-full rounded-full ${tone}`}
          style={{ width: `${String(ratio * 100)}%` }}
        />
      </div>
      <p className="text-sm text-muted-foreground">
        {usage.atLimit
          ? t('billing.usage.full')
          : t('billing.usage.remaining', { count: usage.remaining ?? 0 })}
        {/* Invitations are seats already spent, and saying so here is what stops "but I only
            have four people" being a support call. */}
        {usage.pending > 0 ? ` ${t('billing.usage.pending', { count: usage.pending })}` : ''}
      </p>
    </div>
  );
}

export default UsageMeter;
